import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';

import { filesAPIService } from '~/api/files';
import Accordion from '~/components/ui/Accordion/Accordion';
import ImageUploader from '~/components/ui/ImageUploader/ImageUploader';
import ImageUploaderModal from '~/components/ui/ImageUploaderModal/ImageUploaderModal';
import { Text } from '~/components/ui/text';
import TextInput from '~/components/ui/TextInput';
import { SHOW_LOL_COVER_UPLOAD_BUTTON } from '~/constants/common';
import { useAppDispatch } from '~/hooks/useRedux';
import CreateQRContentBlockLayout from '~/pages/CreateQR/components/CreateQRContentBlockLayout/CreateQRContentBlockLayout';
import { listOfLinksSlice } from '~/store/reducers/previewScreens/list-of-links';
import { updateListOfLinksSiteThunk } from '~/store/reducers/previewScreens/list-of-links';
import { getListOfLinksReducer } from '~/store/reducers/previewScreens/list-of-links/selectors';
import { isMaxLength } from '~/utils/common';
import { logger } from '~/utils/logger';

const linkListForms = [
  {
    label: 'title',
    value: 'Example',
    id: 'h1',
    maxLength: 100,
    isRequired: true,
    placeholder: 'common:enterTitleName',
  },
  {
    label: 'description',
    value: 'Example',
    id: 'h2',
    maxLength: 200,
    placeholder: 'common:enterDescription',
  },
];

export default function BasicInformation() {
  const { t } = useTranslation();
  const [uploaderModal, setUploaderModal] = useState(false);

  const listOfLinksState = useSelector(getListOfLinksReducer);
  const dispatch = useAppDispatch();

  const { actions } = listOfLinksSlice;
  const {
    micrositeData,
    selectedTemplate,
    basicInformation,
    basicInformation: { logoImage, coverImage },
  } = listOfLinksState;

  const addLogoImage = async ({ file, rotate, zoom, roundness }) => {
    if (file?.file) {
      try {
        const storageResponse = await filesAPIService.uploadImage(file.file[0]);

        dispatch(
          updateListOfLinksSiteThunk({
            id: micrositeData?.id,
            body: {
              logo: {
                border_radius: roundness,
                logo_id: storageResponse.data,
                rotate,
                scale: zoom,
              },
            },
            dispatchHandler: () => {
              setUploaderModal(false);
              dispatch(
                actions.setBasicInformation({
                  field: 'logoImage',
                  value: {
                    ...logoImage,
                    url: file?.previewLink,
                    imageStorageId: storageResponse.data,
                    rotate,
                    scale: zoom,
                    borderRadius: roundness,
                  },
                }),
              );
            },
          }),
        );
      } catch (e) {
        logger.error(e);
      }
    } else {
      dispatch(
        updateListOfLinksSiteThunk({
          id: micrositeData?.id,
          body: {
            logo: {
              border_radius: roundness,
              rotate,
              scale: zoom,
            },
          },
          dispatchHandler: () => {
            setUploaderModal(false);
            dispatch(
              actions.setBasicInformation({
                field: 'logoImage',
                value: { ...logoImage, rotate, scale: zoom, borderRadius: roundness },
              }),
            );
          },
        }),
      );
    }
  };

  const addCoverImage = async (event) => {
    const file = event.target.files[0];
    const url = window.URL.createObjectURL(file);

    try {
      const storageResponse = await filesAPIService.uploadImage(file);

      dispatch(
        updateListOfLinksSiteThunk({
          id: micrositeData?.id,
          body: {
            cover_image_id: storageResponse.data,
          },
          dispatchHandler: () =>
            dispatch(
              actions.setBasicInformation({
                field: 'coverImage',
                value: { ...coverImage, url, imageStorageId: storageResponse.data },
              }),
            ),
        }),
      );
    } catch (e) {
      logger.error(e);
    }
  };

  const removeImage = () => {
    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData?.id,
        body: {
          logo: { logo_id: null, scale: 100, borderRadius: 50, rotate: 0 },
        },
        dispatchHandler: () =>
          dispatch(
            actions.setBasicInformation({
              field: 'logoImage',
              value: { url: '', scale: 100, borderRadius: 50, rotate: 0 },
            }),
          ),
      }),
    );
  };

  const removeCoverImage = () => {
    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData?.id,
        body: {
          cover_image_id: null,
        },
        dispatchHandler: () =>
          dispatch(
            actions.setBasicInformation({
              field: 'coverImage',
              value: { url: '' },
            }),
          ),
      }),
    );
  };

  const setLinkInformatiom = (label, id) => {
    if (id === 'h1' && !basicInformation[id]) return;

    dispatch(
      updateListOfLinksSiteThunk({
        id: micrositeData.id,
        body: {
          [label.toLowerCase()]: basicInformation[id],
        },
      }),
    );
  };

  return (
    <CreateQRContentBlockLayout type="basicInfo">
      <Accordion
        defaultExpanded
        variant="child"
        hideBottomBorder
        summary={t('template2:titleLink')}
        details={
          <>
            {!SHOW_LOL_COVER_UPLOAD_BUTTON.includes(selectedTemplate) && (
              <>
                <Text variant="inputSmall" sx={{ marginBottom: '8px', marginTop: '12px' }}>
                  {t('template5:coverImage')}
                </Text>
                <ImageUploader image={coverImage?.url} onDeleteImage={removeCoverImage} onUploadImage={addCoverImage} />
              </>
            )}
            <Text variant="inputSmall" sx={{ marginBottom: '8px', marginTop: '12px' }}>
              {t('template5:yourAvatar')}
            </Text>
            <ImageUploader
              image={logoImage?.url}
              onDeleteImage={removeImage}
              withUploaderModal
              onOpenUploaderModal={() => setUploaderModal(true)}
            />
            {uploaderModal && (
              <ImageUploaderModal
                image={logoImage?.url}
                open={uploaderModal}
                setOpen={setUploaderModal}
                onSave={addLogoImage}
                defaultRotate={logoImage?.rotate}
                defaultRoundness={logoImage?.borderRadius}
                defaultZoom={logoImage?.scale}
              />
            )}

            {linkListForms.map((item, index) => {
              const isTitle = item.id === 'h1';

              return (
                <TextInput
                  defaultValue={basicInformation[item.id]}
                  key={item.label + index}
                  labelSx={{ marginTop: '12px' }}
                  fullWidth
                  placeholder={t(item.placeholder)}
                  label={`${t(`other:${item?.label}`)}${isTitle ? '*' : ''}`}
                  error={!basicInformation[item.id] && item?.isRequired}
                  helperText={
                    (item?.isRequired && !basicInformation[item.id] && t('inputs:required')) ||
                    isMaxLength(basicInformation[item.id], item?.maxLength)
                  }
                  onChange={(e) => dispatch(actions.setBasicInformation({ field: item.id, value: e.target.value }))}
                  onBlur={() => setLinkInformatiom(item.label, item.id)}
                  inputProps={{ maxLength: item?.maxLength }}
                />
              );
            })}
          </>
        }
      />
    </CreateQRContentBlockLayout>
  );
}
